/**
 * The three practice tracks — Foundations, and the Civil and Power and Energy
 * previews — and what they share: which steps a browser has passed, the
 * pages' addresses, and the closed words their analytics may say.
 *
 * docs/foundations.md is the contract. None of this is a credential: the
 * record is per browser, never synced, and only opens the next step.
 */
import { STEP_IDS } from './foundations';
import { CIVIL_STEP_IDS } from './civil';
import { POWER_STEP_IDS } from './power';
import { LEVEL_IDS as GRID_LEVEL_IDS } from './grid/levels';

/** `grid` is the power course's levels (docs/grid.md), on the same record. */
export const TRACK_IDS = ['foundations', 'civil', 'power', 'grid'] as const;
export type TrackId = (typeof TRACK_IDS)[number];

export const TRACK_STEPS: Record<TrackId, readonly string[]> = {
  foundations: STEP_IDS,
  civil: CIVIL_STEP_IDS,
  power: POWER_STEP_IDS,
  grid: GRID_LEVEL_IDS,
};

export const TRACK_PATHS: Record<TrackId, string> = {
  foundations: '/foundations',
  civil: '/civil',
  power: '/power',
  grid: '/grid',
};

/**
 * Hidden since 2026-10-17 (owner): the other engineering branches stay as an
 * experiment we ran, and the product is software engineering.
 * Nothing links here; a track's address goes to the landing page unless it
 * carries `?experiment`. The code, tests and analytics words stay as they are.
 */
export const TRACKS_PUBLIC = false;

export function tracksVisible(search: string): boolean {
  return TRACKS_PUBLIC || new URLSearchParams(search).has('experiment');
}

/** Which track this address is, or null. */
export function trackFromPath(pathname: string): TrackId | null {
  const bare = pathname.replace(/\/+$/, '') || '/';
  return TRACK_IDS.find((t) => TRACK_PATHS[t] === bare) ?? null;
}

const key = (track: TrackId) => (track === 'foundations' ? 'engineeros.foundations' : `engineeros.track.${track}`);

/** Passed steps, in this browser. Empty whenever storage is unavailable. */
export function loadPassed(track: TrackId): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(key(track)) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.filter((id): id is string => TRACK_STEPS[track].includes(id as string)));
  } catch {
    return new Set();
  }
}

export function savePassed(track: TrackId, passed: ReadonlySet<string>): void {
  try {
    localStorage.setItem(key(track), JSON.stringify(TRACK_STEPS[track].filter((id) => passed.has(id))));
  } catch {
    /* private mode, or storage disabled */
  }
}

/**
 * Every `detail` the `practice` event may carry: `<track>:open`,
 * `<track>:<step>` when a step is passed, `<track>:done`. The Worker spells
 * the same list (worker/events.ts `PRACTICE`); the smoke test and
 * web/test/previews.test.ts keep them equal.
 */
export function practiceDetails(): string[] {
  return TRACK_IDS.flatMap((t) => [`${t}:open`, ...TRACK_STEPS[t].map((s) => `${t}:${s}`), `${t}:done`]);
}
